import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Switch,
  FormControlLabel, Snackbar, Alert, List, ListItem, ListItemText, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ChevronLeft, ChevronRight, Today as TodayIcon } from '@mui/icons-material';
import { eventAPI } from '../services/api';
import { errorText } from '../utils/format';
import { localDate, fmtDate, MONTH_NAMES } from '../utils/dates';

const TYPES = {
  HOLIDAY: { label: 'Holiday', bg: '#ffebee', fg: '#c62828' },
  EXAM: { label: 'Exam', bg: '#fff3e0', fg: '#e65100' },
  ACADEMIC: { label: 'Academic', bg: '#e0f2f1', fg: '#00695c' },
  CLINICAL: { label: 'Clinical', bg: '#e1f5fe', fg: '#0277bd' },
  CULTURAL: { label: 'Cultural', bg: '#f3e5f5', fg: '#6a1b9a' },
  SPORTS: { label: 'Sports', bg: '#e8f5e9', fg: '#2e7d32' },
  MEETING: { label: 'Meeting', bg: '#eceff1', fg: '#455a64' },
  OTHER: { label: 'Other', bg: '#fffde7', fg: '#f57f17' },
};

const empty = () => ({ title: '', eventType: 'ACADEMIC', eventDate: localDate(), endDate: '', eventTime: '', venue: '', description: '', published: true });

export default function CalendarManagement() {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);
  const load = useCallback(async () => {
    try { setEvents((await eventAPI.getAll()).data); } catch (e) { notify(errorText(e, 'Could not load the calendar'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const first = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsOn = (day) => {
    const iso = localDate(new Date(cursor.y, cursor.m, day));
    return events.filter((e) => e.eventDate <= iso && iso <= (e.endDate || e.eventDate));
  };

  const shift = (d) => setCursor((c) => { const n = new Date(c.y, c.m + d, 1); return { y: n.getFullYear(), m: n.getMonth() }; });

  const openDialog = (e = null, date = null) => {
    setError('');
    setEditing(e);
    setForm(e ? { title: e.title, eventType: e.eventType || 'OTHER', eventDate: e.eventDate, endDate: e.endDate || '', eventTime: e.eventTime || '', venue: e.venue || '', description: e.description || '', published: e.published }
      : { ...empty(), eventDate: date || localDate() });
    setOpen(true);
  };

  const save = async () => {
    setError('');
    try {
      if (editing) await eventAPI.update(editing.id, form); else await eventAPI.create(form);
      setOpen(false);
      notify(editing ? 'Event updated' : 'Event added to the calendar');
      load();
    } catch (e) { setError(errorText(e, 'Could not save the event')); }
  };

  const remove = async () => {
    try { await eventAPI.remove(confirm.id); notify('Event deleted'); setConfirm(null); load(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const today = localDate();
  const upcoming = events.filter((e) => (e.endDate || e.eventDate) >= today).slice(0, 12);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4">Academic Calendar</Typography>
          <Typography variant="body2" color="text.secondary">Holidays, exams, guest lectures and college events. Published events appear in the Student and Faculty apps.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add event</Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#00796b', color: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton sx={{ color: '#fff' }} onClick={() => shift(-1)}><ChevronLeft /></IconButton>
                <Typography variant="h6" sx={{ minWidth: 190, textAlign: 'center' }}>{MONTH_NAMES[cursor.m]} {cursor.y}</Typography>
                <IconButton sx={{ color: '#fff' }} onClick={() => shift(1)}><ChevronRight /></IconButton>
              </Box>
              <Button size="small" sx={{ color: '#fff' }} startIcon={<TodayIcon />} onClick={() => setCursor({ y: now.getFullYear(), m: now.getMonth() })}>Today</Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: '#e0f2f1' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <Typography key={d} variant="caption" sx={{ p: 1, textAlign: 'center', fontWeight: 800, color: '#004d40' }}>{d}</Typography>)}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {cells.map((day, i) => {
                const iso = day ? localDate(new Date(cursor.y, cursor.m, day)) : null;
                const list = day ? eventsOn(day) : [];
                return (
                  <Box key={i} onClick={() => day && openDialog(null, iso)}
                    sx={{ minHeight: 96, p: 0.75, borderTop: '1px solid #eee', borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid #eee', cursor: day ? 'pointer' : 'default', bgcolor: day ? (iso === today ? '#f1faf9' : '#fff') : '#fafafa', '&:hover': day ? { bgcolor: '#f5f7fa' } : {} }}>
                    {day && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 800, display: 'inline-flex', width: 22, height: 22, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: iso === today ? '#00796b' : 'transparent', color: iso === today ? '#fff' : (i % 7 === 0 ? '#c62828' : 'inherit') }}>{day}</Typography>
                        {list.slice(0, 3).map((e) => {
                          const t = TYPES[e.eventType] || TYPES.OTHER;
                          return <Box key={e.id} onClick={(ev) => { ev.stopPropagation(); openDialog(e); }} sx={{ mt: 0.4, px: 0.6, py: 0.2, borderRadius: 1, bgcolor: t.bg, color: t.fg, fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: e.published ? 1 : 0.5 }}>{e.title}</Box>;
                        })}
                        {list.length > 3 && <Typography variant="caption" color="text.secondary">+{list.length - 3} more</Typography>}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {Object.entries(TYPES).map(([k, t]) => <Chip key={k} size="small" label={t.label} sx={{ bgcolor: t.bg, color: t.fg, fontWeight: 700 }} />)}
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ p: 2 }}>Coming up</Typography>
            <List dense disablePadding>
              {upcoming.map((e) => {
                const t = TYPES[e.eventType] || TYPES.OTHER;
                return (
                  <ListItem key={e.id} divider secondaryAction={
                    <Box><Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(e)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirm(e)}><DeleteIcon fontSize="small" /></IconButton></Tooltip></Box>}>
                    <ListItemText sx={{ pr: 8 }}
                      primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Chip size="small" label={t.label} sx={{ bgcolor: t.bg, color: t.fg, fontWeight: 700, height: 20 }} /><Typography variant="body2" fontWeight={700}>{e.title}</Typography></Box>}
                      secondary={`${fmtDate(e.eventDate)}${e.endDate ? ' to ' + fmtDate(e.endDate) : ''}${e.eventTime ? ' · ' + e.eventTime : ''}${e.venue ? ' · ' + e.venue : ''}${e.published ? '' : ' · Draft'}`} />
                  </ListItem>
                );
              })}
              {upcoming.length === 0 && <ListItem><ListItemText primary="Nothing scheduled" secondary="Add holidays and events so students can plan ahead." /></ListItem>}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{editing ? 'Edit event' : 'Add event'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Type" value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })}>
                {Object.entries(TYPES).map(([k, t]) => <MenuItem key={k} value={k}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="time" label="Time (optional)" InputLabelProps={{ shrink: true }} value={form.eventTime} onChange={(e) => setForm({ ...form, eventTime: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Last day (for multi-day)" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Venue (optional)" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="Details (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />} label="Show in the Student and Faculty apps" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={save}>{editing ? 'Update' : 'Add'}</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete event?</DialogTitle>
        <DialogContent><Typography>"{confirm?.title}" will be removed from the calendar.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
